/**
 * Theme sync: 2026-09-03-base
 * jrs-v2-position-catalogue.js — Position catalogue (STRICT backend-driven)
 *
 * 2026-07-08 (Phase 5). The catalogue LIST + names + per-method variant ids +
 * prices + helper text all come from the backend: Shopify `*_logo_positions`
 * metaobjects (+ the per-product `custom.positions` metafield, + the bundle
 * metaobject's per-category `type_N_positions`) → Liquid
 * `[data-jrs-position-definitions]` JSON tags → `getAll()` builds the descriptors
 * from those tags. There is NO hard-coded fallback list: with no tags / empty
 * metaobjects, `getAll()` returns [] and `hasData()` is false (a console.error
 * fires and the position_picker shows a "positions unavailable" message).
 *
 * Emitters (all now include `description`): jrs-v2-pdp-trigger.liquid,
 * main-product.liquid, main-product-steps.liquid, jrs-v2-customizer-popup.liquid
 * (7 category sets on the cart page), bundle-new.liquid.
 *
 * Backend fields per tag entry: name, handle (optional — the id is re-derived by
 * handleizing the name, so Liquid `-` and JS `_` separators reconcile),
 * embroideryVariantId, printVariantId, embroideryPrice, printPrice, description
 * (→ helper). `application_methods` is DERIVED: a method is offered only when its
 * per-method variant reference exists. `positionVariantId` is resolved per method
 * from the backend variants (method from state.draft.application_methods, default
 * embroidery); 0 + a console.warn when no variant exists (a metaobject data bug).
 *
 * Theme presentation, keyed by position handle (NOT a source list): `ICONS` (chip
 * pictogram; `DEFAULT_ICON` for an unrecognised handle), `RECOMMENDED` (the
 * "Recommended" pill), `DEFAULT_METHOD` (pre-selected method, clamped to available
 * methods), and the garment-outline markers for the collapsible preview. A backend
 * position with an unknown handle still renders — default icon, no pill.
 *
 * See docs/doc-7-per-step-content-catalogue.md §3 (metaobject schema) +
 * docs/doc-8-bundle-structure-access.md (bundle per-category positions).
 */

window.JRS = window.JRS || {};

(function (JRS) {
  'use strict';

  // T-shirt outline path (front view) shared by left_breast, right_breast,
  // left_sleeve, right_sleeve, large_front. Back view used for large_back
  // + nape_of_neck. Marker dot location varies per position.
  var TSHIRT_FRONT_PATH =
    'M14 4 L8 10 L8 18 L14 18 L14 46 L34 46 L34 18 L40 18 L40 10 L34 4 ' +
    'L28 4 C28 7 20 7 20 4 Z';
  var TSHIRT_BACK_PATH =
    'M14 4 L8 10 L8 18 L14 18 L14 46 L34 46 L34 18 L40 18 L40 10 L34 4 ' +
    'L26 4 C25 5.5 23 5.5 22 4 L14 4 Z';

  // Baseball-cap silhouette for headwear bundles. Front = domed crown + a
  // forward-curving peak; back = the same crown with a snapback strap band
  // (centre gap). Same 48×48 box + fill/stroke convention as the t-shirt so
  // the position_picker preview swaps cleanly. Presentational only.
  var CAP_FRONT_PATH =
    'M10 27 C10 13 15 9 24 9 C33 9 38 13 38 27 Z ' +
    'M9 27 C16 30 32 30 39 27 C33 35 15 35 9 27 Z';
  var CAP_BACK_PATH =
    'M10 27 C10 13 15 9 24 9 C33 9 38 13 38 27 Z ' +
    'M14 28 L22 28 L22 32 L14 32 Z M26 28 L34 28 L34 32 L26 32 Z';

  function _svg(path, marker) {
    return (
      '<svg viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
        '<path d="' + path + '" fill="#FFFFFF" stroke="#1A1A1A" stroke-width="1.5" stroke-linejoin="round"/>' +
        marker +
      '</svg>'
    );
  }

  // Marker shapes pulled verbatim from mockups/step2-position-picker-desktop.html.
  // Held in their own map (not inlined into ICONS) so the position_picker's
  // collapsible garment preview can reuse the EXACT same marker geometry +
  // outlines as the per-row thumb — single source of truth. `GARMENT_VIEW`
  // records which silhouette (front/back) each marker belongs on; the preview
  // plots front markers on the front outline and back markers on the back
  // outline rather than fudging both onto one shirt. All presentational —
  // no cart-payload / draft-state / telemetry involvement.
  var MARKERS = {
    left_breast:  '<circle cx="30" cy="22" r="2.8" fill="#FF6B3D"/>',
    right_breast: '<circle cx="18" cy="22" r="2.8" fill="#FF6B3D"/>',
    left_sleeve:  '<circle cx="38" cy="14" r="2.5" fill="#FF6B3D"/>',
    right_sleeve: '<circle cx="11" cy="13" r="2.4" fill="#FF6B3D"/>',
    large_front:  '<circle cx="24" cy="30" r="4.5" fill="#FF6B3D"/>',
    large_back:   '<circle cx="24" cy="30" r="6" fill="none" stroke="#FF6B3D" stroke-width="1.5" stroke-dasharray="2 1.5"/>',
    nape_of_neck: '<circle cx="24" cy="9" r="2.5" fill="none" stroke="#FF6B3D" stroke-width="1.5" stroke-dasharray="2 1.5"/>'
  };

  var GARMENT_VIEW = {
    left_breast: 'front', right_breast: 'front', left_sleeve: 'front',
    right_sleeve: 'front', large_front: 'front',
    large_back: 'back', nape_of_neck: 'back'
  };

  var GARMENT_PATHS = {
    garment:  { front: TSHIRT_FRONT_PATH, back: TSHIRT_BACK_PATH },
    headwear: { front: CAP_FRONT_PATH,    back: CAP_BACK_PATH }
  };

  var ICONS = {
    left_breast:  _svg(TSHIRT_FRONT_PATH, MARKERS.left_breast),
    right_breast: _svg(TSHIRT_FRONT_PATH, MARKERS.right_breast),
    left_sleeve:  _svg(TSHIRT_FRONT_PATH, MARKERS.left_sleeve),
    right_sleeve: _svg(TSHIRT_FRONT_PATH, MARKERS.right_sleeve),
    large_front:  _svg(TSHIRT_FRONT_PATH, MARKERS.large_front),
    large_back:   _svg(TSHIRT_BACK_PATH,  MARKERS.large_back),
    nape_of_neck: _svg(TSHIRT_BACK_PATH,  MARKERS.nape_of_neck)
  };

  /**
   * @typedef {Object} PositionDescriptor
   * @property {string} id - Doc 7 §3.2 handle
   * @property {string} name - Display label
   * @property {number} positionVariantId - Shopify variant id for cart-payload
   *           Doc 2 §2 conformance. Placeholder values until Phase 5
   *           metaobject; pattern 43625637970xxx.
   * @property {string[]} application_methods - Allowed method ids
   * @property {boolean} recommended - Show "Recommended" badge per mockup
   * @property {string} helper - Short description for chip-row helper line
   * @property {string} icon - Inline SVG markup (string) for the chip-row thumb
   */

  // ---------------------------------------------------------------------
  // Theme presentation, keyed by backend position handle (NOT a source list)
  // ---------------------------------------------------------------------
  // The catalogue LIST + names + variants + prices + helper all come from the
  // backend (`[data-jrs-position-definitions]` tags → Shopify metaobjects; see
  // _loadMerchantData). These lookups are pure presentation applied BY HANDLE to
  // whatever positions the backend returns: RECOMMENDED = which get the
  // "Recommended" pill; ICONS (above) = the chip pictogram (DEFAULT_ICON when a
  // handle has no bespoke pictogram); DEFAULT_METHOD (below) = the pre-selected
  // method. A backend position with an unrecognised handle still renders — it
  // just uses the default icon, no pill, embroidery default.
  var RECOMMENDED = { left_breast: true, large_back: true };
  var DEFAULT_ICON = _svg(TSHIRT_FRONT_PATH, '');

  // ---------------------------------------------------------------------
  // Default application method (operator ruling 2026-09-01): the FIRST
  // available method is always the default, on every position. The old
  // smart-default map (large placements -> print) is withdrawn — it made
  // Large Back default to Print while everything else defaulted to
  // Embroidery. Method order is built embroidery-first below, so the
  // default is embroidery wherever both methods exist, and the sole
  // method where a position is locked to one.
  // ---------------------------------------------------------------------

  // ---------------------------------------------------------------------
  // Method copy (2026-06-01) — benefit lines + "Which should I choose?"
  // helper for the application_method step. Held here (not hardcoded in
  // the controller, not in a locale file — the v2 popup has no JS-side
  // translate bridge yet) so copy lives with the position data. Phase 5
  // could migrate to a locale bundle if a JS i18n path is added.
  // ---------------------------------------------------------------------
  var METHOD_COPY = {
    benefit: {
      embroidery: 'Durable, premium finish. Best for text and small or simple logos.',
      print: 'Best for large, colourful, gradient or photographic designs.'
    },
    help: {
      toggle: 'Which should I choose?',
      embroidery: 'Embroidery — stitched thread. Durable and premium; best for text and small or simple logos.',
      print: 'Print — applied to the surface. Best for large designs, photographs, gradients and many colours.'
    }
  };

  // ---------------------------------------------------------------------
  // Merchant variant override (2026-05-19 Phase 2 QA fix, rev 2.1)
  // ---------------------------------------------------------------------
  // Lazy-loads real per-position variant IDs from any
  // `[data-jrs-position-definitions]` tags on the page. Multiple tags may
  // exist on the same page (the V2 popup snippet emits from shop
  // metaobjects with `handle`+`name` keys, the main-product-steps
  // section emits from product metafield with `name` only). All are
  // merged; entries with non-zero variant ids win on collision so
  // partially-zero entries from one source don't shadow good values
  // from another.
  //
  // Position-name → catalogue-id matching uses four strategies in order:
  //   1. entry.handle  — exact match against catalogue id normalised
  //   2. entry.name    — exact match after normalisation
  //   3. compact match — strip all non-alphanumerics, compare both ways
  //   4. substring     — every word of the catalogue id is contained in
  //                       the normalised name (handles "Front Left Breast"
  //                       → "left_breast", "LeftBreast" via compact)
  // Unmatched entries console.warn once each so name drift surfaces in
  // browser DevTools instead of silently breaking cart-add.

  /** @type {Object<string, {id,name,helper,embroideryVariantId,printVariantId,embroideryPrice,printPrice}>|null} */
  var _merchantData = null;
  /** @type {string[]} ordered position ids as discovered in the backend tags */
  var _backendIds = [];
  var _merchantDataLoaded = false;
  var _warnedNames = {};
  var _warnedFallbacks = {};
  var _warnedDuplicates = {};

  // Cart-edit LIST scope (display only — see setActiveSets). `_activeSets` is the
  // list of cart-position-set names the edited line belongs to; `_scopedIds` is the
  // derived set of position ids in those sets ({id:true} map, or null = unscoped).
  //
  // The scope narrows ONLY getAll() — the list the position_picker renders — to the
  // edited product's set(s), so cart-edit shows the product's positions instead of
  // the merged site-wide 7-set the cart page emits. It deliberately does NOT touch
  // getById()/getVariantIdsFor(): the merchant DATA (names, variants, prices) is
  // always read from EVERY tag, so a position's variant/price resolves identically
  // whether shown in the picker or resolved at save — display and the paid line can
  // never disagree, and the save path is byte-identical to before. Set by the shell
  // on cart-edit open (jrs-v2-customizer-popup.js _applyCartEditPositionScope) and
  // cleared on popup close. On a PDP both stay null (single tag → nothing to narrow).
  var _activeSets = null;
  var _scopedIds = null;
  // Per-id position image read from the SCOPED tags only ({id: url}, or null =
  // unscoped). Ids come from the position title, so a title shared across garment
  // sets (e.g. "Large Front" on default + apron) merges into one entry whose image
  // is the FIRST tag's (default → a t-shirt diagram on an apron). When a scope is
  // active the descriptor prefers this map, so the picker shows the edited
  // product's own diagram. Display only: variants/prices are untouched.
  var _scopedImages = null;

  function _normaliseName(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function _compactName(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '');
  }

  // Under the strict backend-driven model the backend handle IS the catalogue id
  // (there is no hard-coded list to match against). Prefer the explicit handle;
  // otherwise handleize the display name (`_compactName` retained for tests/other
  // callers).
  function _deriveId(entry) {
    if (!entry) return '';
    return _normaliseName(entry.handle || entry.name || '');
  }

  // Compute the id whitelist for the current _activeSets by reading ONLY the
  // [data-jrs-position-definitions] tags whose data-jrs-cart-position-set matches,
  // and deriving each entry's id the same way _loadMerchantData does. Returns an
  // {id:true} map, or null = "don't narrow" (unscoped, or FAIL-OPEN: no matching
  // tag / zero usable ids — a mis-scope or an empty scoped metaobject degrades to
  // the full list rather than a blank picker). Recomputed by setActiveSets; the
  // full merchant DATA is never touched, so getById/getVariantIdsFor stay complete.
  function _computeScopedIds() {
    _scopedImages = null;
    if (!_activeSets || !_activeSets.length) return null;
    if (typeof document === 'undefined') return null;
    var want = {};
    for (var s = 0; s < _activeSets.length; s++) want[String(_activeSets[s])] = true;
    var tags = document.querySelectorAll('[data-jrs-position-definitions]');
    var ids = {};
    var images = {};
    var count = 0;
    for (var t = 0; t < tags.length; t++) {
      var tag = tags[t];
      var setName = tag.getAttribute && tag.getAttribute('data-jrs-cart-position-set');
      if (!setName || !want[setName]) continue;
      if (!tag.textContent) continue;
      var raw;
      try { raw = JSON.parse(tag.textContent); }
      catch (parseErr) { continue; }
      if (!Array.isArray(raw)) continue;
      for (var j = 0; j < raw.length; j++) {
        var entry = raw[j];
        if (!entry || (!entry.name && !entry.handle)) continue;
        var id = _deriveId(entry);
        // hasOwnProperty guard: a position id equal to an Object.prototype member
        // name (e.g. "constructor") must not read as already-present via the proto.
        if (id && !Object.prototype.hasOwnProperty.call(ids, id)) { ids[id] = true; count++; }
        // First non-empty image within the scoped set(s) wins.
        if (id && typeof entry.image === 'string' && entry.image &&
            !Object.prototype.hasOwnProperty.call(images, id)) {
          images[id] = entry.image;
        }
      }
    }
    if (count === 0) {
      if (window.console && typeof console.warn === 'function') {
        console.warn(
          'jrs-v2-position-catalogue: cart-edit scope [' + _activeSets.join(', ') +
          '] resolved no positions on the page; showing the full list instead.'
        );
      }
      return null; // fail open — never a blank picker
    }
    _scopedImages = images;
    return ids;
  }

  // Build the catalogue ENTIRELY from the backend `[data-jrs-position-definitions]`
  // tags (Shopify metaobjects → Liquid). The tag entries ARE the list; there is no
  // hard-coded fallback list. Returns a byId map { id → { id, name, helper,
  // embroideryVariantId, printVariantId, embroideryPrice, printPrice } } and records
  // the discovery order in `_backendIds`. Strict: no tags / no usable entries →
  // console.error + empty (the picker renders a "positions unavailable" state), never
  // a silent placeholder list.
  function _loadMerchantData() {
    if (_merchantDataLoaded) return _merchantData;
    _merchantDataLoaded = true;
    _backendIds = [];
    try {
      if (typeof document === 'undefined') return null;
      var tags = document.querySelectorAll('[data-jrs-position-definitions]');
      if (!tags || tags.length === 0) {
        if (window.console && typeof console.error === 'function') {
          console.error(
            'jrs-v2-position-catalogue: no [data-jrs-position-definitions] tags on the page. ' +
            'The position catalogue is backend-driven (Shopify *_logo_positions metaobjects → ' +
            'the pdp-trigger / popup / bundle Liquid). With no tags there are no positions to show. ' +
            'Confirm the customizer snippet is rendered and the metaobjects are populated.'
          );
        }
        return null;
      }

      var byId = {};
      var ids = [];

      for (var t = 0; t < tags.length; t++) {
        var tag = tags[t];
        if (!tag.textContent) continue;
        var raw;
        try { raw = JSON.parse(tag.textContent); }
        catch (parseErr) {
          if (window.console && typeof console.warn === 'function') {
            console.warn('jrs-v2-position-catalogue: failed to parse position tag', t, parseErr);
          }
          continue;
        }
        if (!Array.isArray(raw) || raw.length === 0) continue;

        for (var j = 0; j < raw.length; j++) {
          var entry = raw[j];
          if (!entry || (!entry.name && !entry.handle)) continue;
          var id = _deriveId(entry);
          if (!id) continue;

          var emb = parseInt(entry.embroideryVariantId, 10) || 0;
          var prn = parseInt(entry.printVariantId, 10) || 0;
          // Per-method prices in pence (Shopify variant.price). `null` = the tag
          // carries no price for that method → the picker renders no price (NOT "Free").
          var embPrice = (typeof entry.embroideryPrice === 'number') ? entry.embroideryPrice : null;
          var prnPrice = (typeof entry.printPrice === 'number') ? entry.printPrice : null;
          var nm = (typeof entry.name === 'string' && entry.name) ? entry.name : id;
          var helper = (typeof entry.description === 'string') ? entry.description : '';
          // Position metaobject image URL (the same `position.image` the live
          // theme renders). Optional — '' when the tag carries no image, in
          // which case the picker falls back to the generated SVG icon.
          var img = (typeof entry.image === 'string') ? entry.image : '';

          var existing = byId[id];
          if (existing) {
            // Merge across tags (the cart page emits one tag per garment category):
            // non-zero variant + non-null price + non-empty name/helper/image win, so
            // a partial tag never shadows a good value from another.
            existing.embroideryVariantId = emb > 0 ? emb : existing.embroideryVariantId;
            existing.printVariantId = prn > 0 ? prn : existing.printVariantId;
            existing.embroideryPrice = embPrice !== null ? embPrice : existing.embroideryPrice;
            existing.printPrice = prnPrice !== null ? prnPrice : existing.printPrice;
            if ((!existing.name || existing.name === existing.id) && nm) existing.name = nm;
            if (!existing.helper && helper) existing.helper = helper;
            if (!existing.image && img) existing.image = img;
          } else {
            byId[id] = {
              id: id,
              name: nm,
              helper: helper,
              image: img,
              embroideryVariantId: emb,
              printVariantId: prn,
              embroideryPrice: embPrice,
              printPrice: prnPrice
            };
            ids.push(id);
          }
        }
      }

      _merchantData = byId;
      _backendIds = ids;

      if (ids.length === 0) {
        if (window.console && typeof console.error === 'function') {
          console.error(
            'jrs-v2-position-catalogue: ' + tags.length + ' position tag(s) present but no usable ' +
            'entries parsed — the metaobjects may be empty. The picker will show no positions.'
          );
        }
      } else if (window.console && typeof console.info === 'function') {
        console.info(
          'jrs-v2-position-catalogue: loaded ' + ids.length + ' position(s) from ' +
          tags.length + ' backend tag(s).',
          { ids: ids }
        );
      }

      return _merchantData;
    } catch (e) {
      if (window.console && typeof console.warn === 'function') {
        console.warn('jrs-v2-position-catalogue: unexpected error loading position data', e);
      }
      _backendIds = [];
      return null;
    }
  }

  /**
   * Resolve the real variant id for a position, choosing between
   * embroidery and print based on the customer's current state.draft
   * selection. Falls back to the placeholder when merchant data is
   * unavailable (no script tag on the page — cart edits, etc.) or the
   * specific position lacks a merchant entry.
   *
   * @param {PositionDescriptor} descriptor
   * @returns {number}
   */
  function _resolvePositionVariantId(descriptor) {
    var merchant = _loadMerchantData();
    var data = merchant && merchant[descriptor.id];
    if (!data) {
      _warnFallback(descriptor.id, 'no backend entry');
      return 0;
    }

    // Chosen application method for this position; default embroidery before the
    // application_method step has run.
    var method = 'embroidery';
    try {
      if (window.JRS && JRS.CustomizerPopupState &&
          typeof JRS.CustomizerPopupState.getState === 'function') {
        var state = JRS.CustomizerPopupState.getState();
        var methodMap = ((state && state.draft) || {}).application_methods || {};
        if (methodMap[descriptor.id] === 'print' || methodMap[descriptor.id] === 'embroidery') {
          method = methodMap[descriptor.id];
        }
      }
    } catch (_e) { /* swallow */ }

    if (method === 'print' && data.printVariantId) return data.printVariantId;
    if (data.embroideryVariantId) return data.embroideryVariantId;
    if (data.printVariantId) return data.printVariantId;
    _warnFallback(descriptor.id, 'backend entry has no variant id');
    return 0;
  }

  function _warnFallback(positionId, reason) {
    var key = positionId + '|' + reason;
    if (_warnedFallbacks[key]) return;
    _warnedFallbacks[key] = true;
    if (window.console && typeof console.warn === 'function') {
      console.warn(
        'jrs-v2-position-catalogue: no usable variant id for position "' + positionId +
        '" (' + reason + '). Cart-add will fail with "Cannot find variant" — check the ' +
        'position metaobject\'s embroidery/print variant references.'
      );
    }
  }

  // A position whose embroidery + print variant references are the SAME id can
  // only produce one decoration SKU — offering both methods lets a customer pick
  // a method that ships as the other (e.g. "Print" selected → the Embroidery
  // variant added, as seen on a real Large Front cart line). Logged loudly (once
  // per position) so the metaobject's print variant gets a distinct id in admin.
  function _warnDuplicateVariant(positionId, variantId) {
    if (_warnedDuplicates[positionId]) return;
    _warnedDuplicates[positionId] = true;
    if (window.console && typeof console.error === 'function') {
      console.error(
        'jrs-v2-position-catalogue: position "' + positionId + '" has identical ' +
        'embroidery + print variant ids (' + variantId + '). A method that maps to ' +
        'the other method\'s SKU would ship the wrong decoration, so only ONE method ' +
        'is being offered. Fix the position metaobject to give print its own variant.'
      );
    }
  }

  /**
   * 2026-05-20 Bug 2 fix — expose per-method merchant prices to step
   * controllers. Returns an object with both prices (in pence) plus
   * the method-resolved price (per state.draft.application_methods,
   * same lookup as _resolvePositionVariantId). null values indicate
   * "merchant data didn't provide this price"; controllers should
   * NOT default to "Free" when null — render no price instead.
   *
   * @param {PositionDescriptor} descriptor
   * @returns {{embroideryPrice: number|null, printPrice: number|null, resolvedPrice: number|null, method: string|null}}
   */
  function _resolvePositionPrices(descriptor) {
    var merchant = _loadMerchantData();
    var data = merchant && merchant[descriptor.id];
    var embPrice = data && typeof data.embroideryPrice === 'number' ? data.embroideryPrice : null;
    var prnPrice = data && typeof data.printPrice === 'number' ? data.printPrice : null;

    // Resolve which method to highlight (mirrors _resolvePositionVariantId's
    // method-resolution: state.draft.application_methods[id] → 'embroidery'
    // default → null when no draft state is available, e.g. on initial
    // position_picker render before user has chosen any methods).
    var method = null;
    try {
      if (window.JRS && JRS.CustomizerPopupState &&
          typeof JRS.CustomizerPopupState.getState === 'function') {
        var state = JRS.CustomizerPopupState.getState();
        var methodMap = ((state && state.draft) || {}).application_methods || {};
        if (methodMap[descriptor.id] === 'print' || methodMap[descriptor.id] === 'embroidery') {
          method = methodMap[descriptor.id];
        }
      }
    } catch (_e) { /* swallow */ }

    var resolved;
    if (method === 'print') resolved = prnPrice;
    else if (method === 'embroidery') resolved = embPrice;
    else resolved = (embPrice !== null) ? embPrice : prnPrice; // default-to-embroidery preview
    return {
      embroideryPrice: embPrice,
      printPrice: prnPrice,
      resolvedPrice: typeof resolved === 'number' ? resolved : null,
      method: method
    };
  }

  /**
   * Build a full descriptor for a backend position id: the backend supplies
   * name/variants/prices/helper; the theme supplies icon/recommended/default_method
   * (by handle) + method availability derived from which per-method variants exist.
   *
   * @param {string} id
   * @returns {PositionDescriptor}
   */
  function _buildDescriptor(id) {
    var data = (_merchantData && _merchantData[id]) ||
      { id: id, name: id, helper: '', embroideryVariantId: 0, printVariantId: 0, embroideryPrice: null, printPrice: null };

    // Method availability is backend-derived: a method is offered only if its
    // per-method variant reference exists on the metaobject.
    var methods = [];
    if (data.embroideryVariantId) methods.push('embroidery');
    if (data.printVariantId) methods.push('print');
    if (methods.length === 0) methods = ['embroidery']; // degenerate (no variants) — still list it; warns on resolve
    // Integrity guard: identical embroidery + print variant ids → one SKU, so a
    // method choice can ship the wrong decoration. Collapse to a single method
    // (embroidery — the base method the shared SKU almost always is) and log.
    if (data.embroideryVariantId && data.printVariantId &&
        data.embroideryVariantId === data.printVariantId) {
      _warnDuplicateVariant(id, data.embroideryVariantId);
      methods = ['embroidery'];
    }

    var defaultMethod = methods[0];

    var descriptor = {
      id: id,
      name: data.name || id,
      positionVariantId: 0, // filled below (method-aware)
      application_methods: methods,
      default_method: defaultMethod,
      recommended: !!RECOMMENDED[id],
      helper: data.helper || '',
      // Real position photo from the metaobject (same image the live theme
      // renders); '' when none — the picker then falls back to `icon`.
      // A scoped set's own image wins over the cross-set merge (see _scopedImages).
      image: (_scopedImages && Object.prototype.hasOwnProperty.call(_scopedImages, id))
        ? _scopedImages[id]
        : ((typeof data.image === 'string') ? data.image : ''),
      icon: ICONS[id] || DEFAULT_ICON,
      marker: MARKERS[id] || '',
      view: GARMENT_VIEW[id] || 'front',
      embroideryPrice: (typeof data.embroideryPrice === 'number') ? data.embroideryPrice : null,
      printPrice: (typeof data.printPrice === 'number') ? data.printPrice : null,
      resolvedPrice: null
    };
    descriptor.positionVariantId = _resolvePositionVariantId(descriptor);
    descriptor.resolvedPrice = _resolvePositionPrices(descriptor).resolvedPrice;
    return descriptor;
  }

  /**
   * Returns the full catalogue, built entirely from the backend position tags.
   * Empty when no tags / no usable entries (a data problem — see the console.error
   * in _loadMerchantData; callers should surface a "positions unavailable" state).
   *
   * @returns {PositionDescriptor[]}
   */
  function getAll() {
    _loadMerchantData();
    var out = [];
    for (var i = 0; i < _backendIds.length; i++) {
      var id = _backendIds[i];
      // Cart-edit list scope: skip positions outside the edited product's set(s).
      // _scopedIds is null (no narrowing) on a PDP and whenever the scope fails
      // open. Descriptors are still built from the FULL merchant data, so a scoped
      // position's variant/price is identical to what save resolves.
      if (_scopedIds && !Object.prototype.hasOwnProperty.call(_scopedIds, id)) continue;
      out.push(_buildDescriptor(id));
    }
    return out;
  }

  /**
   * Look up a single descriptor by id (backend handle). Returns null on miss.
   *
   * @param {string} id
   * @returns {PositionDescriptor|null}
   */
  function getById(id) {
    if (typeof id !== 'string' || id.length === 0) return null;
    _loadMerchantData();
    if (!_merchantData || !_merchantData[id]) return null;
    return _buildDescriptor(id);
  }

  /**
   * True when the backend supplied at least one position. The position-picker uses
   * this to render a "positions unavailable" message instead of a blank list.
   *
   * @returns {boolean}
   */
  function hasData() {
    _loadMerchantData();
    return _backendIds.length > 0;
  }

  /**
   * Per-method branding variant ids for a position, straight from the merchant
   * data ([data-jrs-position-definitions]). Unlike descriptor.positionVariantId
   * (which is resolved for the CURRENT state.draft method), this exposes BOTH
   * method variants so a caller can emit a method the customer didn't pick —
   * needed by the bundle_wide per-child method engine (D2b) when a child is
   * forced to a non-desired method. Returns { embroidery, print } with 0 for
   * any method the metaobject doesn't carry a variant for. Read-only; no state.
   *
   * @param {string} id - catalogue/backend position id
   * @returns {{embroidery: number, print: number}}
   */
  function getVariantIdsFor(id) {
    if (typeof id !== 'string' || id.length === 0) return { embroidery: 0, print: 0 };
    _loadMerchantData();
    var data = _merchantData && _merchantData[id];
    return {
      embroidery: (data && data.embroideryVariantId) || 0,
      print: (data && data.printVariantId) || 0
    };
  }

  // ---------------------------------------------------------------------
  // Cart-edit list scoping (see _activeSets / _scopedIds / _computeScopedIds)
  // ---------------------------------------------------------------------
  // Narrow the LIST getAll() returns to one or more cart-position sets so the
  // cart-edit position_picker shows only the edited product's positions. The set
  // name(s) are resolved in Liquid and stamped on the cart row (cart-item.liquid)
  // and read by the shell at Edit time. Passing an empty / invalid list — or a
  // scope that resolves no positions — clears the scope (full list). Does NOT scope
  // getById/getVariantIdsFor, so variant/price resolution (and the paid line) is
  // unchanged; only the picker's list narrows.
  function setActiveSets(names) {
    var list = [];
    if (Object.prototype.toString.call(names) === '[object Array]') {
      for (var i = 0; i < names.length; i++) {
        var n = names[i];
        if (typeof n === 'string') n = n.replace(/^\s+|\s+$/g, '');
        if (typeof n === 'string' && n && list.indexOf(n) === -1) list.push(n);
      }
    }
    _activeSets = list.length ? list : null;
    // Resolve the id whitelist now - the cart-page tags are in the DOM at Edit
    // time. Null when unscoped or on fail-open. The merchant DATA cache is untouched
    // (getById/getVariantIdsFor stay complete), so no invalidation is needed.
    _scopedIds = _computeScopedIds();
  }

  function clearActiveSets() {
    if (_activeSets === null && _scopedIds === null) return; // no-op fast path (PDP)
    _activeSets = null;
    _scopedIds = null;
    _scopedImages = null;
  }

  JRS.PositionCatalogue = {
    getAll: getAll,
    getById: getById,
    getVariantIdsFor: getVariantIdsFor,
    hasData: hasData,
    // Cart-edit list scoping — narrow getAll() to the edited product's set(s).
    // clearActiveSets() (or an empty/unmatched list) restores the full list.
    setActiveSets: setActiveSets,
    clearActiveSets: clearActiveSets,
    // Test/diagnostic seam — the current scope, or null when unscoped.
    _getActiveSets: function () { return _activeSets ? _activeSets.slice() : null; },
    // Method copy (benefit lines + "Which should I choose?" helper) for
    // the application_method step. Returns a deep-ish copy so callers
    // can't mutate the source. Shape:
    //   { benefit: { embroidery, print }, help: { toggle, embroidery, print } }
    getMethodCopy: function () {
      return {
        benefit: {
          embroidery: METHOD_COPY.benefit.embroidery,
          print: METHOD_COPY.benefit.print
        },
        help: {
          toggle: METHOD_COPY.help.toggle,
          embroidery: METHOD_COPY.help.embroidery,
          print: METHOD_COPY.help.print
        }
      };
    },
    // Garment-outline `d` for the position_picker's collapsible preview.
    // Presentational only — returns the front or back silhouette path so the
    // controller can plot per-position markers (descriptor.marker) on the
    // matching silhouette (descriptor.view). `type` selects the family:
    // 'headwear' → cap silhouette; anything else (default) → t-shirt.
    getGarmentOutlinePath: function (view, type) {
      var set = GARMENT_PATHS[type] || GARMENT_PATHS.garment;
      return (view === 'back') ? set.back : set.front;
    },
    // Diagnostic helper — call from browser console to see what merchant
    // data was loaded and how each catalogue position resolves.
    // Forces a fresh read each time. Returns:
    //   { tagsFound, merchantData, resolved: { <id>: { embroidery, print, current } } }
    _debugMerchantData: function () {
      _merchantData = null;
      _merchantDataLoaded = false;
      _backendIds = [];
      _warnedNames = {};
      _warnedFallbacks = {};
      _warnedDuplicates = {};
      var data = _loadMerchantData();
      var tagsFound = 0;
      try { tagsFound = document.querySelectorAll('[data-jrs-position-definitions]').length; }
      catch (_e) {}
      var resolved = {};
      for (var i = 0; i < _backendIds.length; i++) {
        var id = _backendIds[i];
        resolved[id] = {
          backend: (data && data[id]) || null,
          currentResolved: _resolvePositionVariantId({ id: id })
        };
      }
      return { tagsFound: tagsFound, positionCount: _backendIds.length, merchantData: data, resolved: resolved };
    }
  };
})(window.JRS);
